import {DatePicker} from '@github-ui/date-picker'
import {useCallback, useMemo, useState} from 'react'

import {formatDateString} from '../../utils/date-formatting'
import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import {LABELS} from './constants/labels'
import fieldEditorsStyles from './FieldEditors.module.css'

// Normalized hour for timezone-safe date storage (noon UTC)
export const NORMALIZED_HOUR = 12

const DateDisplay = ({formattedDate}: {formattedDate: string}) =>
  formattedDate ? (
    <span className={fieldEditorsStyles.dateText}>{formattedDate}</span>
  ) : (
    <span className={fieldEditorsStyles.noValueContainer}>{LABELS.setValue}</span>
  )

export type IssueFieldDateEditorProps = {
  fieldId: string
  fieldName: string
  value: Date | null
  readonly: boolean
  onCommit: (fieldId: string, value: Date | null) => void
  shouldOpen?: boolean
  isDraft?: boolean
  errorPresent?: boolean
  className?: string
}

export const IssueFieldDateEditor = ({
  fieldId,
  fieldName,
  value,
  readonly,
  onCommit,
  shouldOpen = false,
  isDraft = false,
  errorPresent = false,
  className,
}: IssueFieldDateEditorProps) => {
  const formattedDate = useMemo(() => {
    if (!value) return ''
    return formatDateString(value)
  }, [value])

  // Convert UTC-normalized date to local date for DatePicker display
  // The value prop is a Date at noon UTC, but DatePicker needs a local Date
  // with the correct calendar day to highlight the right day in the picker
  const datePickerValue = useMemo(() => {
    if (!value) return null
    // Extract UTC date components and create local Date with same calendar date
    return new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate())
  }, [value])

  const [isOpen, setIsOpen] = useState(shouldOpen)

  const onClose = useCallback(() => {
    if (isDraft) {
      onCommit(fieldId, null)
    }
    setIsOpen(false)
  }, [isDraft, onCommit, fieldId])

  const handleChange = (date: Date | null) => {
    // Normalize the date to avoid timezone issues before passing to onCommit
    // This ensures the selected date is preserved regardless of user timezone
    let normalizedDate: Date | null = null
    if (date) {
      // Create a new date using the same year, month, and day but normalized to avoid timezone shifts
      normalizedDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), NORMALIZED_HOUR, 0, 0, 0))
    }

    onCommit(fieldId, normalizedDate)
    setIsOpen(false)
  }

  return (
    <Section
      sectionHeader={
        <SectionHeader
          title={fieldName}
          readonly={readonly}
          buttonProps={readonly ? undefined : {onClick: () => setIsOpen(true)}}
          errorPresent={errorPresent}
        />
      }
      className={className}
    >
      {readonly ? (
        <div className={fieldEditorsStyles.dateAnchorContainer}>
          <DateDisplay formattedDate={formattedDate} />
        </div>
      ) : (
        <DatePicker
          key={fieldName}
          anchor={props => (
            <div
              {...props}
              className={`${fieldEditorsStyles.dateAnchorContainer} ${fieldEditorsStyles.dateAnchorContainerInteractive}`}
            >
              <DateDisplay formattedDate={formattedDate} />
            </div>
          )}
          showClearButton
          showTodayButton
          variant="single"
          dateFormat="long"
          onChange={handleChange}
          value={datePickerValue}
          placeholder="Set date"
          open={isOpen}
          onOpen={() => setIsOpen(true)}
          onClose={onClose}
        />
      )}
    </Section>
  )
}

try{ DateDisplay.displayName ||= 'DateDisplay' } catch {}
try{ IssueFieldDateEditor.displayName ||= 'IssueFieldDateEditor' } catch {}